<template>
  <div class="sticker-card">
    <div class="cat-ear left-ear"></div>
    <div class="cat-ear right-ear"></div>
    <div class="sticker-image" @click.stop="openDetailDialog">
      <img :src="sticker.imageUrl" :alt="sticker.title">
    </div>
    <div class="sticker-title" :title="sticker.title">
      {{ sticker.title || '无名哈基米' }}
    </div>
    <div class="sticker-footer">
      <div class="sticker-stats">
        <span class="likes tooltip" 
              @click.stop="handleLike" 
              :class="{ 'voted': hasLiked }"
              :title="hasLiked ? '已点赞' : '点赞'"
        >
          <i class="icon-heart"></i> {{ localLikes }}
          <span class="tooltip-text">{{ hasLiked ? '已点赞' : '点赞' }}</span>
        </span>
        <span class="dislikes tooltip" 
              @click.stop="handleDislike"
              :class="{ 'voted': hasDisliked }"
              :title="hasDisliked ? '已点踩' : '点踩'"
        >
          <i class="icon-dislikes"></i> {{ localDislikes }}
          <span class="tooltip-text">{{ hasDisliked ? '已点踩' : '点踩' }}</span>
        </span>
        <span class="download tooltip" @click.stop="downloadImage">
          <i class="icon-download"></i>
          <span class="tooltip-text">下载</span>
        </span>
        <span v-if="isLoggedIn" class="delete tooltip" @click.stop="handleDelete">
          <i class="icon-delete"></i>
          <span class="tooltip-text">删除</span>
        </span>
      </div>
    </div>
    
    <div v-if="showDeleteConfirm" class="delete-confirm-overlay" @click.self="cancelDelete">
      <div class="delete-confirm-dialog">
        <div class="delete-confirm-header">
          <h3>确认删除</h3>
        </div>
        <div class="delete-confirm-body">
          <p>确定要删除这个表情包吗？此操作不可恢复。</p>
        </div>
        <div class="delete-confirm-footer">
          <button class="cancel-button" @click="cancelDelete">取消</button>
          <button class="confirm-button" @click="confirmDelete" :disabled="isDeleting">
            {{ isDeleting ? '删除中...' : '确认删除' }}
          </button>
        </div>
      </div>
    </div>
  </div>
  
  <!-- 详情对话框放到组件外部，避免嵌套出现的问题 -->
  <teleport to="body">
    <div v-if="showDetailDialog" class="detail-dialog-overlay" @click.self="closeDetailDialog">
      <div class="detail-dialog">
        <div class="detail-dialog-header">
          <h3>{{ sticker.title || '无名哈基米' }}</h3>
          <button class="close-button" @click="closeDetailDialog">&times;</button>
        </div>
        <div class="detail-dialog-body">
          <div class="detail-image">
            <img :src="sticker.imageUrl" :alt="sticker.title">
          </div>
          <div class="detail-code-section">
            <div class="code-group">
              <div class="code-header">
                <h4>图片URL</h4>
                <button 
                  class="copy-button" 
                  @click="copyText(sticker.imageUrl, $event)"
                  :class="{ 'copied': lastCopiedText === sticker.imageUrl }"
                >复制</button>
              </div>
              <div class="code-content">
                <code>{{ sticker.imageUrl }}</code>
              </div>
            </div>
            
            <div class="code-group">
              <div class="code-header">
                <h4>Markdown 格式</h4>
                <button 
                  class="copy-button" 
                  @click="copyText(markdownCode, $event)"
                  :class="{ 'copied': lastCopiedText === markdownCode }"
                >复制</button>
              </div>
              <div class="code-content">
                <code>{{ markdownCode }}</code>
              </div>
            </div>
            
            <div class="code-group">
              <div class="code-header">
                <h4>HTML 格式</h4>
                <button 
                  class="copy-button" 
                  @click="copyText(htmlCode, $event)"
                  :class="{ 'copied': lastCopiedText === htmlCode }"
                >复制</button>
              </div>
              <div class="code-content">
                <code>{{ htmlCode }}</code>
              </div>
            </div>
            
            <div class="code-group">
              <div class="code-header">
                <h4>BBCode 格式</h4>
                <button 
                  class="copy-button" 
                  @click="copyText(bbCode, $event)"
                  :class="{ 'copied': lastCopiedText === bbCode }"
                >复制</button>
              </div>
              <div class="code-content">
                <code>{{ bbCode }}</code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { stickerApi } from '../services/api';
import authService from '../services/auth';

export default {
  name: 'StickerCard',
  inject: ['showNotification'],
  emits: ['sticker-deleted'], // 添加事件声明
  props: {
    sticker: {
      type: Object,
      required: true,
      default: () => ({
        id: 0,
        title: '加载中...',
        imageUrl: '',
        likes: 0,
        dislikes: 0,
        createdAt: '',
        type: 0
      })
    }
  },
  data() {
    return {
      hasLiked: false,
      hasDisliked: false,
      isLikeLoading: false,
      isDislikeLoading: false,
      localLikes: 0,
      localDislikes: 0,
      showDeleteConfirm: false,
      isDeleting: false,
      showDetailDialog: false,
      lastCopiedText: '',
      isImageLoaded: false,
      showInlineNotification: false,
      inlineNotificationMessage: ''
    }
  },
  computed: {
    likedStorageKey() {
      return `sticker_liked_${this.sticker.id}`;
    },
    dislikedStorageKey() {
      return `sticker_disliked_${this.sticker.id}`;
    },
    isLoggedIn() {
      return authService.isAuthenticated();
    },
    markdownCode() {
      return `![${this.sticker.title || '哈基米表情'}](${this.sticker.imageUrl})`;
    },
    htmlCode() {
      return `<img src="${this.sticker.imageUrl}" alt="${this.sticker.title || '哈基米表情'}" />`;
    },
    bbCode() {
      return `[img]${this.sticker.imageUrl}[/img]`;
    }
  },
  created() {
    this.localLikes = this.sticker.likes;
    this.localDislikes = this.sticker.dislikes;
    this.loadVoteStatusFromStorage();
  },
  mounted() {
    // 添加监听登录状态变化的事件
    window.addEventListener('auth-state-changed', this.handleAuthStateChange);
  },
  beforeUnmount() {
    // 移除事件监听
    window.removeEventListener('auth-state-changed', this.handleAuthStateChange);
  },
  methods: {
    loadVoteStatusFromStorage() {
      this.hasLiked = localStorage.getItem(this.likedStorageKey) === 'true';
      this.hasDisliked = localStorage.getItem(this.dislikedStorageKey) === 'true';
    },
    saveVoteStatusToStorage() {
      if (this.hasLiked) {
        localStorage.setItem(this.likedStorageKey, 'true');
      }
      if (this.hasDisliked) {
        localStorage.setItem(this.dislikedStorageKey, 'true');
      }
    },
    async handleLike() {
      if (this.isLikeLoading || this.hasLiked) return;
      try {
        this.isLikeLoading = true;
        const response = await stickerApi.voteSticker(this.sticker.id, 1);
        if (response.data && response.data.code === 200) {
          this.hasLiked = true;
          this.localLikes += 1;
          this.saveVoteStatusToStorage();
          this.showNotification('点赞成功', 'success');
        } else {
          throw new Error('API返回格式错误');
        }
      } catch (error) {
        console.error('点赞失败:', error);
        this.showNotification('操作失败，请稍后重试', 'error');
      } finally {
        this.isLikeLoading = false;
      }
    },
    async handleDislike() {
      if (this.isDislikeLoading || this.hasDisliked) return;
      try {
        this.isDislikeLoading = true;
        const response = await stickerApi.voteSticker(this.sticker.id, 0);
        if (response.data && response.data.code === 200) {
          this.hasDisliked = true;
          this.localDislikes += 1;
          this.saveVoteStatusToStorage();
          this.showNotification('点踩成功', 'info');
        } else {
          throw new Error('API返回格式错误');
        }
      } catch (error) {
        console.error('点踩失败:', error);
        this.showNotification('操作失败，请稍后重试', 'error');
      } finally {
        this.isDislikeLoading = false;
      }
    },
    async downloadImage() {
      console.log(`下载表情包 ID: ${this.sticker.id}`);
      try {
        const response = await fetch(this.sticker.imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = this.sticker.title || `sticker-${this.sticker.id}.png`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.showNotification('下载成功', 'success');
      } catch (err) {
        console.error('下载图片失败:', err);
        this.showNotification('下载失败，请稍后重试', 'error');
      }
    },
    handleDelete() {
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.showDeleteConfirm = false;
    },
    async confirmDelete() {
      if (this.isDeleting) return;
      this.isDeleting = true;
      
      try {
        const response = await stickerApi.deleteSticker(this.sticker.id);
        
        if (response.data && response.data.code === 200) {
          this.showNotification('表情包删除成功', 'success');
          this.showDeleteConfirm = false;
          // 触发表情包被删除的事件，让父组件处理UI更新
          this.$emit('sticker-deleted', this.sticker.id);
        } else {
          throw new Error('删除失败');
        }
      } catch (error) {
        console.error('删除表情包失败:', error);
        
        let errorMessage = '删除失败，请稍后重试';
        if (error.response) {
          if (error.response.status === 401) {
            errorMessage = '无权限执行此操作，请重新登录';
            // 清除token，重新登录
            authService.logout();
          } else if (error.response.data && error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }
        
        this.showNotification(errorMessage, 'error');
      } finally {
        this.isDeleting = false;
      }
    },
    getFileName(url) {
      if (!url) return '';
      const parts = url.split('/');
      let fileName = parts[parts.length - 1];
      if (fileName.includes('?')) {
        fileName = fileName.split('?')[0];
      }
      return fileName || '';
    },
    // 处理登录状态变化
    handleAuthStateChange(event) {
      // 更新组件内的登录状态
      if (event.detail && event.detail.isLoggedIn !== undefined) {
        // 强制更新登录状态检查
        this.$forceUpdate();
      }
    },
    // 打开详情对话框
    openDetailDialog() {
      this.showDetailDialog = true;
      // 防止事件冒泡导致立即关闭
      setTimeout(() => {
        document.body.style.overflow = 'hidden'; // 防止背景滚动
      }, 10);
    },
    // 关闭详情对话框
    closeDetailDialog() {
      this.showDetailDialog = false;
      document.body.style.overflow = ''; // 恢复背景滚动
    },
    // 复制文本到剪贴板
    async copyText(text, event) {
      try {
        let button = null;
        if (event && event.target) {
          button = event.target;
        }
        
        // 检查 Clipboard API 是否可用
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // 回退方法：使用传统的DOM方法复制文本
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          
          const successful = document.execCommand('copy');
          document.body.removeChild(textarea);
          
          if (!successful) {
            throw new Error('复制命令执行失败');
          }
        }
        
        this.lastCopiedText = text;
        
        // 创建一个临时的提示元素
        const toast = document.createElement('div');
        toast.textContent = "✓ 已复制";
        toast.className = "copy-toast";
        document.body.appendChild(toast);
        
        // 如果有按钮，则定位到按钮附近
        if (button) {
          const rect = button.getBoundingClientRect();
          const buttonCenter = rect.left + (rect.width / 2); // 计算按钮的中心位置
          
          toast.style.position = 'fixed';
          toast.style.zIndex = '10000';
          toast.style.left = `${buttonCenter}px`;
          toast.style.top = `${rect.top - 30}px`;
          toast.style.transform = 'translateX(-50%)'; // 水平居中对齐
        } else {
          // 否则居中显示
          toast.style.position = 'fixed';
          toast.style.zIndex = '10000';
          toast.style.top = '50%';
          toast.style.left = '50%';
          toast.style.transform = 'translate(-50%, -50%)';
        }
        
        // 2秒后移除提示
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 2000);
        
        // 切换按钮状态
        setTimeout(() => {
          this.lastCopiedText = '';
        }, 3000);
      } catch (error) {
        console.error('复制失败:', error);
        this.showNotification('复制失败，请手动复制', 'error');
      }
    }
  },
  watch: {
    'sticker.likes'(newVal) {
      if (!this.hasLiked) {
        this.localLikes = newVal;
      }
    },
    'sticker.dislikes'(newVal) {
      if (!this.hasDisliked) {
        this.localDislikes = newVal;
      }
    }
  }
}
</script>

<style scoped>
.sticker-card {
  border: 1px solid #e8f5e9;
  border-radius: 8px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background-color: #fff;
  position: relative;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
}

.cat-ear {
  position: absolute;
  top: 0px;
  width: 20px;
  height: 20px;
  background-color: #42b983;
  border-radius: 50% 50% 0 0;
  z-index: 1;
}

.left-ear {
  left: 15px;
  transform: rotate(-30deg);
}

.right-ear {
  right: 15px;
  transform: rotate(30deg);
}

.sticker-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px rgba(66, 185, 131, 0.2);
}

.sticker-card:hover .cat-ear {
  animation: earWiggle 0.5s;
}

@keyframes earWiggle {
  0% { transform: rotate(-30deg); }
  50% { transform: rotate(-40deg); }
  100% { transform: rotate(-30deg); }
}

.sticker-card:hover .cat-ear.right-ear {
  animation: rightEarWiggle 0.5s;
}

@keyframes rightEarWiggle {
  0% { transform: rotate(30deg); }
  50% { transform: rotate(40deg); }
  100% { transform: rotate(30deg); }
}

.sticker-image {
  width: 100%;
  padding-bottom: 100%;
  position: relative;
  background-color: #f1f8e9;
  overflow: hidden;
  cursor: pointer;
}

.sticker-image img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.sticker-title {
  padding: 8px 10px;
  font-size: 14px;
  text-align: center;
  color: #333;
  background-color: #f9f9f9;
  border-top: 1px solid #e8f5e9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.sticker-footer {
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e8f5e9;
  background-color: #f9f9f9;
}

.sticker-stats {
  display: flex;
  font-size: 12px;
  color: #666;
  width: 100%;
  justify-content: space-around;
}

.sticker-stats span {
  margin-right: 0;
  display: flex;
  align-items: center;
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s, transform 0.1s, color 0.2s;
}

.sticker-stats span:hover {
  background-color: #f5f5f5;
}

.sticker-stats span:active {
  transform: scale(0.95);
}

.likes:active {
  color: #ff5252;
}

.dislikes:active {
  color: #42b983;
}

.likes.voted {
  color: #ff5252;
  font-weight: bold;
  cursor: default;
}

.dislikes.voted {
  color: #42b983;
  font-weight: bold;
  cursor: default;
}

.icon-heart::before {
  content: "❤️";
}

.icon-comment::before {
  content: "💬";
}

.icon-dislikes::before {
  content: "🐾";
}

.icon-copy::before {
  content: "📋";
}

.icon-download::before {
  content: "⬇️";
}

.icon-delete::before {
  content: "🗑️";
}

.copy {
  position: relative;
}

.download {
  position: relative;
}

.copy-success::after {
  content: "✓";
  position: absolute;
  top: -15px;
  right: -5px;
  color: #42b983;
  font-weight: bold;
  font-size: 14px;
  animation: fadeOut 1s forwards;
}

.download-success::after {
  content: "✓";
  position: absolute;
  top: -15px;
  right: -5px;
  color: #42b983;
  font-weight: bold;
  font-size: 14px;
  animation: fadeOut 1s forwards;
}

@keyframes fadeOut {
  0% { opacity: 1; }
  100% { opacity: 0; }
}

.dislikes-button button {
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.dislikes-button button:hover {
  background-color: #3da576;
}

.tooltip {
  position: relative;
}

.tooltip-text {
  visibility: hidden;
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
  text-align: center;
  border-radius: 4px;
  padding: 5px 8px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 12px;
  white-space: nowrap;
}

.tooltip:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.7) transparent transparent transparent;
}

.delete-confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.delete-confirm-dialog {
  background-color: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
}

.delete-confirm-header {
  padding: 16px;
  border-bottom: 1px solid #e8f5e9;
}

.delete-confirm-header h3 {
  margin: 0;
  color: #ff5252;
  font-size: 18px;
}

.delete-confirm-body {
  padding: 20px 16px;
}

.delete-confirm-body p {
  margin: 0;
  color: #333;
}

.delete-confirm-footer {
  padding: 16px;
  border-top: 1px solid #e8f5e9;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-button {
  padding: 8px 16px;
  background-color: #f5f5f5;
  color: #333;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.confirm-button {
  padding: 8px 16px;
  background-color: #ff5252;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.confirm-button:hover:not(:disabled) {
  background-color: #ff1744;
}

.confirm-button:disabled {
  background-color: #ffcdd2;
  cursor: not-allowed;
}

.cancel-button:hover {
  background-color: #e0e0e0;
}

.delete.tooltip:hover {
  color: #ff5252;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-5px); }
  40%, 80% { transform: translateX(5px); }
}

.delete.tooltip:active i {
  animation: shake 0.5s;
}

/* 详情对话框样式 */
</style>
<style>
/* 未使用 scoped，让它能应用到 teleport 外的元素 */
.detail-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001; /* 确保在最顶层 */
}

.detail-dialog {
  background-color: #fff;
  border-radius: 8px;
  width: 95%;
  max-width: 1100px; /* 增大最大宽度 */
  max-height: 90vh; /* 增大最大高度 */
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

.detail-dialog-header {
  padding: 16px;
  background-color: #42b983;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-dialog-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  padding: 0 8px;
  line-height: 1;
}

.detail-dialog-body {
  display: flex;
  flex-direction: row;
  overflow: hidden;
  flex: 1;
  max-height: calc(90vh - 56px); /* 调整大小以匹配新的最大高度 */
}

@media (max-width: 768px) {
  .detail-dialog-body {
    flex-direction: column;
  }
}

.detail-image {
  flex: 1;
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f5f5;
  overflow: hidden;
}

.detail-image img {
  max-width: 100%;
  max-height: 500px; /* 增大图片的最大高度 */
  object-fit: contain;
}

.detail-code-section {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 400px; /* 确保代码区域有足够的宽度 */
}

.code-group {
  background-color: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #edf2f7;
  padding: 8px 12px;
  border-bottom: 1px solid #e8e8e8;
}

.code-header h4 {
  margin: 0;
  font-size: 14px;
  color: #333;
}

.copy-button {
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s;
  position: relative;
}

.copy-button:hover {
  background-color: #3da576;
}

.copy-button.copied {
  background-color: #2c7c58;
}

.code-content {
  padding: 16px; /* 增加内边距 */
  overflow-x: auto;
}

.code-content code {
  font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
  color: #333;
  font-size: 14px; /* 增大字体大小 */
  word-break: break-all;
  white-space: pre-wrap;
  display: block;
  width: 100%;
}
</style>
<style>
/* 全局样式，用于复制提示 */
.copy-toast {
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  position: fixed;
  z-index: 10000; /* 超高层级确保显示在最上层 */
  animation: fadeInOut 2s forwards;
  pointer-events: none; /* 确保不会阻碍其他交互 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

@keyframes fadeInOut {
  0% { opacity: 0; transform: translateY(10px); }
  20% { opacity: 1; transform: translateY(0); }
  80% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-10px); }
}
</style>